import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<Button>` replaces `.btn-add`, `.btn-primary`,
 * `.btn-cancel`, `.btn-sm`, `.btn-back`, `.btn-adv`, `.aud-cta-btn`,
 * `.wr-gen-btn`, `.ta-done`, `.ta-star`, `.ta-collab`, `.ta-open`,
 * `.post-btn` and every other ad-hoc button styling across pages.
 *
 * Variants cover the four functional buckets seen in the CSS:
 *   primary     — filled accent (action CTAs)
 *   secondary   — outlined subdued (cancel / back)
 *   ghost       — transparent, hover tint (inline nav/toggles)
 *   destructive — red-tinted (delete / remove)
 *   success     — green-tinted (mark done)
 *   warn        — amber-tinted (star / attention)
 * Sizes: xs|sm|md|lg map to the densities `.ta`, `.btn-sm`, default, `.factions`.
 */
const button = cva(
  [
    "inline-flex items-center gap-1.5 font-sans font-semibold rounded-lg",
    "border cursor-pointer transition-colors",
    "disabled:opacity-40 disabled:cursor-not-allowed",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-[rgba(59,127,250,0.15)] border-[rgba(59,127,250,0.3)] text-rc-accent-strong",
          "hover:bg-[rgba(59,127,250,0.28)]",
        ].join(" "),
        secondary: [
          "bg-rc-surface-hover border-rc-border text-rc-text-secondary",
          "hover:bg-rc-border hover:text-rc-text-primary",
        ].join(" "),
        ghost: [
          "bg-transparent border-transparent text-rc-text-secondary",
          "hover:bg-rc-surface-hover hover:text-rc-text-primary",
        ].join(" "),
        destructive: [
          "bg-[rgba(239,68,68,0.1)] border-[rgba(240,68,68,0.24)] text-rc-red",
          "hover:bg-[rgba(239,68,68,0.18)]",
        ].join(" "),
        success: [
          "bg-[rgba(16,201,129,0.12)] border-[rgba(16,201,129,0.24)] text-[#10c981]",
          "hover:bg-[rgba(16,201,129,0.2)]",
        ].join(" "),
        warn: [
          "bg-[rgba(245,158,11,0.08)] border-[rgba(245,158,11,0.2)] text-[#d97706]",
          "hover:bg-[rgba(245,158,11,0.16)]",
        ].join(" "),
      },
      size: {
        xs: "px-2.5 py-1 text-[11px] rounded-md",
        sm: "px-3 py-1.5 text-xs",
        md: "px-4 py-2.5 text-[13.5px]",
        lg: "px-6 py-2.5 text-sm",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

const Button = forwardRef(function Button({ className, variant, size, type = "button", children, ...props }, ref) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(button({ variant, size }), className)}
      {...props}
    >
      {children}
    </button>
  );
});

export default Button;
